// OP-3619 — Escolher a peça certa entre as disponíveis da praça.
//
// A lista parava num recorte por id (50 na separação, 200 na transferência) e
// não dizia que tinha cortado: quem estava com a peça seguinte na mão não
// conseguia escolhê-la. A busca estreita por serial ou patrimônio no servidor,
// dentro do mesmo contexto, e o hook diz quando houve corte. Erro de consulta
// não é ausência de estoque.
import { FC, ReactNode } from 'react'
import { DisponivelRow } from '../../../types/estoque'
import { BD, Button, Spinner } from '../styles'
import { CampoComLeitor } from './CampoComLeitor'
import { mesmoCodigo } from './leitura'

export interface ConsultaDePecas {
  pecas: DisponivelRow[]
  cortou: boolean
  /** O termo que produziu `pecas` — a busca já assentada, não o que está sendo digitado. */
  termo: string
  isLoading: boolean
  isError: unknown
  mutate: () => unknown
}

export interface SeletorDePecasProps {
  consulta: ConsultaDePecas
  busca: string
  aoBuscar: (valor: string) => void
  /** Já escolhidas neste documento e ainda disponíveis no estoque: aparecem com ✓. */
  escolhidas?: number[]
  /** Já saíram do estoque por este documento: não aparecem, nem numa lista em cache. */
  ocultas?: number[]
  aoEscolher: (peca: DisponivelRow) => void
  disabled?: boolean
  mostrarPatrimonio?: boolean
  vazio: ReactNode
}

export const SeletorDePecas: FC<SeletorDePecasProps> = ({
  consulta, busca, aoBuscar, escolhidas = [], ocultas = [], aoEscolher, disabled, mostrarPatrimonio, vazio,
}) => {
  const { cortou, termo } = consulta
  const pecas = ocultas.length ? consulta.pecas.filter((u) => ocultas.indexOf(u.id) < 0) : consulta.pecas
  const aguardando = busca.trim() !== termo
  const exata = (u: DisponivelRow): boolean => mesmoCodigo(u.serial, termo) || mesmoCodigo(u.patrimonio, termo)
  return (
    <div>
      <CampoComLeitor
        valor={busca}
        aoMudar={aoBuscar}
        tituloLeitor="Ler a peça"
        placeholder="Buscar por serial ou patrimônio"
        rotulo="Buscar peça por serial ou patrimônio"
        style={{ marginBottom: 8 }}
      />
      {aguardando ? (
        <div style={{ fontSize: 13, color: BD.grayText }}>Buscando…</div>
      ) : consulta.isLoading ? <Spinner /> : consulta.isError ? (
        <div style={{ fontSize: 13, color: BD.red }}>
          Não foi possível carregar as peças disponíveis nesta praça.{' '}
          <Button $variant="secondary" disabled={disabled} onClick={() => consulta.mutate()}>Tentar de novo</Button>
        </div>
      ) : pecas.length === 0 ? (
        termo
          ? <div style={{ fontSize: 13, color: BD.grayText }}>Nenhuma peça disponível com “{termo}” nesta praça.</div>
          : <>{vazio}</>
      ) : (
        <>
          {pecas.some((u) => exata(u) && escolhidas.indexOf(u.id) < 0) ? (
            <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>É esta? Toque na peça destacada para confirmar.</div>
          ) : pecas.some(exata) ? (
            <div style={{ marginBottom: 6, fontSize: 13, color: BD.grayText }}>Esta peça já foi escolhida.</div>
          ) : null}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {pecas.map((u) => {
              const escolhida = escolhidas.indexOf(u.id) >= 0
              return (
                <Button key={u.id} $variant={escolhida ? 'primary' : 'secondary'} disabled={disabled || escolhida} onClick={() => aoEscolher(u)}
                        style={exata(u) && !escolhida ? { boxShadow: `0 0 0 3px ${BD.red}` } : undefined}>
                  {escolhida ? '✓ ' : ''}{u.serial}{mostrarPatrimonio && u.patrimonio ? ` · ${u.patrimonio}` : ''}
                </Button>
              )
            })}
          </div>
          {cortou && (
            <div style={{ marginTop: 8, fontSize: 12, color: BD.grayText }}>
              Há mais peças disponíveis além destas: busque pelo serial ou patrimônio para achar a que está com você.
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default SeletorDePecas
